import { Box, Stack, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import { NoDataWarning } from "@/components/common";
import { ScreenMobile } from "@/components/common/ScreenMobile";
import type { TableActions, TableColumn } from "@/components/GenericTable/types";
import { ScheduleSliderDotsBalloon } from "@/components/schedule/ScheduleSliderDotsBalloon";
import { DashViewAllButton } from "@/pages/home/components/PatientDash/components/DashViewAllButton";
import { EhrSummaryMobileSection } from "./EhrSummaryMobileSection";
import { EhrSummaryRecordCard } from "./EhrSummaryRecordCard";

/** Quantidade exibida no preview mobile antes de "Ver tudo". */
export const EHR_SUMMARY_PREVIEW_LIMIT = 5;

interface EhrSummaryMobileListProps<T extends Record<string, any>> {
  title: string;
  columns: TableColumn[];
  rows: T[];
  actions?: TableActions<T>;
  /** Oculta cabeçalho da seção (útil dentro de abas). */
  hideSectionHeader?: boolean;
  previewLimit?: number;
  /** Card customizado (ex.: DocumentCard). */
  renderCard?: (row: T, index: number) => ReactNode;
}

function renderCardItem<T extends Record<string, any>>(
  row: T,
  index: number,
  columns: TableColumn[],
  actions?: TableActions<T>,
  renderCard?: (row: T, index: number) => ReactNode
) {
  if (renderCard) {
    return renderCard(row, index);
  }

  return (
    <EhrSummaryRecordCard
      row={row}
      columns={columns}
      actions={typeof actions === "function" ? actions : undefined}
      index={index}
    />
  );
}

function getRowKey<T extends Record<string, any>>(row: T, index: number) {
  return String(row.id ?? row.encounterId ?? row.key ?? index);
}

interface EhrSummaryMobileCarouselProps<T extends Record<string, any>> {
  items: T[];
  columns: TableColumn[];
  actions?: TableActions<T>;
  renderCard?: (row: T, index: number) => ReactNode;
  /** Reinicia slide ao trocar aba ou lista. */
  resetKey?: string | number;
}

/** Carousel horizontal — um card por vez, dots e contador. */
function EhrSummaryMobileCarousel<T extends Record<string, any>>({
  items,
  columns,
  actions,
  renderCard,
  resetKey,
}: EhrSummaryMobileCarouselProps<T>) {
  const [activeSlide, setActiveSlide] = useState(0);
  const sliderRef = useRef<HTMLDivElement | null>(null);
  const isSliderScrolling = useRef(false);

  const scrollSliderToIndex = useCallback(
    (index: number) => {
      const el = sliderRef.current;
      if (!el || items.length <= 1) return;
      isSliderScrolling.current = true;
      el.scrollTo({ left: index * el.clientWidth, behavior: "smooth" });
      window.setTimeout(() => {
        isSliderScrolling.current = false;
      }, 320);
    },
    [items.length]
  );

  const handleSliderScroll = useCallback(() => {
    if (isSliderScrolling.current) return;
    const el = sliderRef.current;
    if (!el || el.clientWidth <= 0) return;
    const nextIndex = Math.round(el.scrollLeft / el.clientWidth);
    if (nextIndex !== activeSlide && nextIndex >= 0 && nextIndex < items.length) {
      setActiveSlide(nextIndex);
    }
  }, [activeSlide, items.length]);

  useEffect(() => {
    setActiveSlide(0);
    if (sliderRef.current) sliderRef.current.scrollLeft = 0;
  }, [items.length, resetKey]);

  if (items.length === 0) return null;

  if (items.length === 1) {
    return (
      <Box sx={{ width: "100%", minWidth: 0 }}>
        {renderCardItem(items[0], 0, columns, actions, renderCard)}
      </Box>
    );
  }

  return (
    <Stack spacing={0.85} sx={{ width: "100%", minWidth: 0, overflow: "hidden" }}>
      <Box
        ref={sliderRef}
        onScroll={handleSliderScroll}
        sx={{
          display: "flex",
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          overflowX: "auto",
          overflowY: "hidden",
          scrollSnapType: "x mandatory",
          scrollBehavior: "smooth",
          WebkitOverflowScrolling: "touch",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          alignItems: "stretch",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {items.map((row, index) => (
          <Box
            key={getRowKey(row, index)}
            sx={{
              flex: "0 0 100%",
              width: "100%",
              maxWidth: "100%",
              minWidth: 0,
              scrollSnapAlign: "start",
              scrollSnapStop: "always",
              boxSizing: "border-box",
            }}
          >
            {renderCardItem(row, index, columns, actions, renderCard)}
          </Box>
        ))}
      </Box>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        spacing={1}
        sx={{ position: "relative", width: "100%" }}
      >
        <ScheduleSliderDotsBalloon
          total={items.length}
          activeIndex={activeSlide}
          onDotClick={(index) => {
            setActiveSlide(index);
            scrollSliderToIndex(index);
          }}
          compact
        />

        <Typography
          variant="caption"
          color="text.secondary"
          fontWeight={800}
          sx={{
            position: "absolute",
            right: 0,
            fontSize: "0.6875rem",
            letterSpacing: "0.02em",
          }}
        >
          {activeSlide + 1}/{items.length}
        </Typography>
      </Stack>
    </Stack>
  );
}

/** Lista vertical — usada no modal "Ver tudo". */
function EhrSummaryMobileVerticalList<T extends Record<string, any>>({
  items,
  columns,
  actions,
  renderCard,
}: EhrSummaryMobileCarouselProps<T>) {
  if (items.length === 0) return null;

  return (
    <Stack spacing={1.25} sx={{ width: "100%", minWidth: 0 }}>
      {items.map((row, index) => (
        <Box key={getRowKey(row, index)} sx={{ width: "100%", minWidth: 0 }}>
          {renderCardItem(row, index, columns, actions, renderCard)}
        </Box>
      ))}
    </Stack>
  );
}

/** Lista mobile com carousel + tela "Ver tudo". */
export function EhrSummaryMobileList<T extends Record<string, any>>({
  title,
  columns,
  rows,
  actions,
  hideSectionHeader = false,
  previewLimit = EHR_SUMMARY_PREVIEW_LIMIT,
  renderCard,
}: EhrSummaryMobileListProps<T>) {
  const [allListOpen, setAllListOpen] = useState(false);
  const safeRows = rows ?? [];

  const previewItems = useMemo(
    () => safeRows.slice(0, previewLimit),
    [safeRows, previewLimit]
  );

  const hasMore = safeRows.length > previewLimit;

  const listBody =
    safeRows.length === 0 ? (
      <NoDataWarning />
    ) : (
      <EhrSummaryMobileCarousel
        items={previewItems}
        columns={columns}
        actions={actions}
        renderCard={renderCard}
        resetKey={`${title}-${previewItems.length}`}
      />
    );

  const sectionContent = (
    <>
      {listBody}
      {hasMore ? (
        <DashViewAllButton
          onClick={() => setAllListOpen(true)}
          count={safeRows.length}
        />
      ) : null}

      <ScreenMobile
        open={allListOpen}
        onClose={() => setAllListOpen(false)}
        title={title}
      >
        <Box sx={{ px: 1.5, pb: 2, pt: 0.5, width: "100%" }}>
          {safeRows.length === 0 ? (
            <NoDataWarning />
          ) : (
            <EhrSummaryMobileVerticalList
              items={safeRows}
              columns={columns}
              actions={actions}
              renderCard={renderCard}
              resetKey={`${title}-full-${safeRows.length}`}
            />
          )}
        </Box>
      </ScreenMobile>
    </>
  );

  if (hideSectionHeader) {
    return <Box sx={{ width: "100%" }}>{sectionContent}</Box>;
  }

  return (
    <EhrSummaryMobileSection
      title={title}
      subtitle={
        safeRows.length > 0
          ? `${safeRows.length} registro${safeRows.length === 1 ? "" : "s"}`
          : "Nenhum registro"
      }
    >
      {sectionContent}
    </EhrSummaryMobileSection>
  );
}
